import { useCallback, useEffect, useRef } from 'react';

import { createMaster, createVoice, curveVelocity, getRingTime, loadTone, resumeContext } from '../utils/toneVoices';
import { getChordMidiNotes, getChordRhythmNotes, getMelodyNote } from '../utils/chordBarsMidi';
import { getBeatsPerBar } from '../utils/songTiming';

/**
 * Sounding one chord, on its own, right now — the ear's answer to "what did I just write".
 *
 * Everything else in the app plays a *clip*: a run of notes handed to Tone's transport and released
 * by a Part callback. A chord tapped on the chart has no clip and no clock — it is a single gesture
 * that has to make a sound before the finger leaves the screen. So this keeps a voice of its own,
 * builds it on the first tap and holds it, and never touches the transport. That last part is what
 * lets it live alongside useChordGridPlayer: that hook's teardown calls transport.cancel(), which
 * would take any transport-scheduled audition down with it mid-playback.
 *
 * The voice outlives each tap deliberately. A sampled preset costs a Sampler construction and a
 * Tone.loaded() wait to build, which is nothing once but is the whole latency budget of a tap.
 *
 * @param {string} instrumentId - Preset to audition with; changing it rebuilds the voice
 * @param {boolean} isActive - Whether the modal holding it is open. Releases the voice when false.
 * @param {object} [options] - { rhythm, bpm, timeSignature }. `rhythm` is the id of a pattern from
 *   chordRhythmPatterns.js, or empty for one block. With one, a tap plays a bar of the chord as that
 *   style would play it — the same pattern, from the same builder, the preview clip uses.
 * @returns {function(string): void} Plays one chord symbol. Unparseable symbols make no sound.
 */

// Long enough to hear the chord as a chord rather than as a blip, short enough that tapping along a
// line doesn't stack four of them on top of each other
const AUDITION_SECONDS = 1.4;

// The chart is being read, not performed — the same even velocity buildChordBarsMidi writes
const VELOCITY = 0.8;

// What a rhythm tap is played at when the song has no tempo of its own. A chart with no BPM can't be
// previewed at all, but it can still be tapped, and a pattern has to move at some speed to be one.
const AUDITION_BPM = 100;

// A tap is a gesture, not a performance: at 60bpm a full 4/4 bar would hold the finger for four
// seconds, against the 1.4 a block chord takes. Past this the bar is played short instead.
const MAX_RHYTHM_SECONDS = 2.5;

/** The bar a rhythm tap plays: how fast the pattern moves, and how much of the bar there is time for. */
const getAuditionBar = (bpm, timeSignature) => {
  const tempo = Number(bpm) > 0 ? Number(bpm) : AUDITION_BPM;
  const secondsPerBeat = 60 / tempo;
  const beatsPerBar = getBeatsPerBar(timeSignature);

  // Two beats is the floor: below that a tap is a single strike, and the one thing every pattern
  // here exists to say is what the hands do against each other. A bar-length pattern cut this short
  // is only its opening, which is the honest thing for a tap to be — the preview plays the rest.
  const beats = Math.min(beatsPerBar, Math.max(2, Math.floor(MAX_RHYTHM_SECONDS / secondsPerBeat)));

  return { beats, beatsPerBar, secondsPerBeat };
};

const useChordAudition = (instrumentId, isActive = false, { rhythm = '', bpm, timeSignature = '4/4' } = {}) => {
  const toneRef = useRef(null);
  const voiceRef = useRef(null);
  const effectsRef = useRef(null);
  const masterRef = useRef(null);
  // The build in flight, so two taps a few milliseconds apart share one voice rather than each
  // constructing their own and the second orphaning the first
  const buildingRef = useRef(null);

  const teardown = useCallback(() => {
    buildingRef.current = null;

    if (voiceRef.current) {
      voiceRef.current.releaseAll();
      voiceRef.current.dispose();
      voiceRef.current = null;
    }

    // Between the voice and the master chain, matching where they sit in the signal path
    if (effectsRef.current) {
      effectsRef.current.forEach((node) => node.dispose());
      effectsRef.current = null;
    }

    if (masterRef.current) {
      masterRef.current.dispose();
      masterRef.current = null;
    }
  }, []);

  // A voice built for one instrument can't play another, and a closed modal shouldn't hold a
  // sampler open
  useEffect(() => {
    if (!isActive) teardown();
  }, [isActive, instrumentId, teardown]);

  useEffect(() => teardown, [teardown]);

  return useCallback(
    (chord, { melody = false } = {}) => {
      const blockNotes = (midis) => midis.map((midi) => ({ midi, time: 0, duration: AUDITION_SECONDS, velocity: VELOCITY }));
      const melodyNote = melody ? getMelodyNote(chord) : null;

      // Every mode comes out as the same note shape, so there is one trigger loop below rather than
      // one per mode. A block chord is that shape with everything landing at once, and a melody
      // note is one note of it — played plain, whatever the rhythm.
      let notes;

      if (melody) notes = melodyNote === null ? [] : blockNotes([melodyNote]);
      else if (rhythm) notes = getChordRhythmNotes(chord, { patternId: rhythm, ...getAuditionBar(bpm, timeSignature) });
      else notes = blockNotes(getChordMidiNotes(chord));

      if (notes.length === 0) return;

      const build = async () => {
        const Tone = toneRef.current || (await loadTone());
        toneRef.current = Tone;

        if (!(await resumeContext(Tone))) return null;

        if (voiceRef.current) return { Tone, voice: voiceRef.current };

        const master = createMaster(Tone);
        const { voice, effects } = await createVoice(Tone, instrumentId, master);

        masterRef.current = master;
        effectsRef.current = effects;
        voiceRef.current = voice;

        return { Tone, voice };
      };

      buildingRef.current = buildingRef.current || build();

      buildingRef.current
        .then((built) => {
          buildingRef.current = null;

          // No built voice means the context never started — a browser waiting on a gesture it
          // didn't count. Silence here is the honest answer; the next tap tries again.
          if (!built) return;

          const { Tone, voice } = built;
          const now = Tone.now();

          // A rhythm tap runs for most of a bar, so tapping along a line would lay one pattern
          // over the next. Cutting the last one first is what keeps a run of taps sounding like a
          // run of chords — and a note stopped before it was due to start never sounds at all, so
          // this takes the queued half of the pattern down with it.
          if (rhythm) voice.releaseAll();

          notes.forEach((note) => {
            voice.triggerAttackRelease(
              Tone.Frequency(note.midi, 'midi').toFrequency(),
              // A pedalled style rings past the key coming up, here as much as in the grid player
              getRingTime(note),
              now + note.time,
              curveVelocity(note.velocity),
            );
          });
        })
        .catch((error) => {
          // Otherwise indistinguishable from a chord the parser simply had nothing to say about
          console.error('Chord audition failed', error);
          buildingRef.current = null;
          teardown();
        });
    },
    [instrumentId, rhythm, bpm, timeSignature, teardown],
  );
};

export default useChordAudition;
